import { Location } from '../../location';
import { getPageMetaData } from '../../utils/get-page-meta-data';

/**
 * PageLinkLocation - Dynamic Link Discovery System
 *
 * This class uses a DYNAMIC approach where it re-searches for links each time getElements() is called,
 * rather than storing static DOM element references. This design change was made to solve critical
 * mobile menu integration issues.
 *
 * KEY CHANGES FROM ORIGINAL IMPLEMENTATION:
 * - Constructor now accepts CSS selectors instead of pre-found DOM elements
 * - getElements() actively searches the DOM on each call instead of returning static elements
 * - Includes special logic for mobile menu containers that may load dynamically
 * - Tracks processed links via data-wiapiprocessed attribute to avoid duplicate processing
 * - Can detect and reprocess regenerated links
 *
 * WHY THIS APPROACH IS NECESSARY:
 * 1. Mobile menus are often loaded/regenerated dynamically by JavaScript frameworks
 * 2. Static element references become stale when DOM is modified
 * 3. Mobile menu links may be separate DOM elements from desktop counterparts
 * 4. XTime integration requires onclick handlers on ALL menu instances (desktop + mobile)
 *
 * MOBILE MENU DETECTION:
 * The system searches within specific mobile menu containers to ensure mobile menu links
 * are discovered even when they're dynamically created or separate from desktop links.
 */
export class PageLinkLocation extends Location {
	constructor(searchSelectors) {
		super();
		this.searchSelectors = searchSelectors;
	}

	getMeta() {
		return getPageMetaData();
	}

	async getElements() {
		// Re-search for links each time to pick up dynamically loaded content
		const allLinks = [];
		const processedLinks = new Set(); // Track by element reference to avoid true duplicates

		// Search in document first
		this.searchSelectors.forEach(selector => {
			const links = Array.from(document.querySelectorAll(selector));

			// Filter out links that have already been processed
			const unprocessedLinks = links.filter(
				link => !link.dataset.wiapiProcessed
			);

			// Also check for links that were previously processed but might have been regenerated
			const regeneratedLinks = links.filter(
				link => link.dataset.wiapiProcessed
			);
			if (regeneratedLinks.length > 0) {
				regeneratedLinks.forEach(link => {
					// Remove the processed marker so they can be reprocessed
					link.removeAttribute('data-wiapiprocessed');
					unprocessedLinks.push(link);
				});
			}

			unprocessedLinks.forEach(link => {
				if (!processedLinks.has(link)) {
					allLinks.push(link);
					processedLinks.add(link);
				}
			});
		});

		// Also search specifically in mobile menu containers if they exist
		// This is important for mobile menu links that might be separate elements
		const mobileMenuSelectors = [
			'.ddc-mobile-fragment-nav',
			'[data-location~="primary-menu"]',
			'.mobile-menu',
			'#ddc-mobile-header-nav'
		];

		mobileMenuSelectors.forEach(mobileContainer => {
			const container = document.querySelector(mobileContainer);
			if (container) {
				this.searchSelectors.forEach(selector => {
					const links = Array.from(container.querySelectorAll(selector));

					// For mobile menu links, include ALL unprocessed links, even if they appear to be duplicates
					// This is crucial because mobile menu links might be different DOM elements
					const unprocessedLinks = links.filter(
						link => !link.dataset.wiapiProcessed
					);

					// Check for regenerated mobile menu links
					const regeneratedMobileLinks = links.filter(
						link => link.dataset.wiapiProcessed
					);
					if (regeneratedMobileLinks.length > 0) {
						regeneratedMobileLinks.forEach(link => {
							link.removeAttribute('data-wiapiprocessed');
							unprocessedLinks.push(link);
						});
					}

					unprocessedLinks.forEach(link => {
						// Always add mobile menu links if they haven't been processed, even if they appear to be duplicates
						if (!processedLinks.has(link)) {
							allLinks.push(link);
							processedLinks.add(link);
						}
					});
				});
			}
		});

		return allLinks;
	}
}
